// SignalCard.jsx — Individual signal display card (light theme)
import React, { useState } from "react";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";

const OUTCOME_STYLE = {
  WINNER:      { label: "✓ Winner",    cls: "bg-green-100 text-green-700 border-green-300" },
  BIG_WINNER:  { label: "★ Big Win",   cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  T1_HIT:      { label: "🎯 T1 Hit",   cls: "bg-blue-100 text-blue-700 border-blue-300" },
  LOSER:       { label: "✗ Loser",     cls: "bg-red-100 text-red-700 border-red-300" },
  EXPIRED:     { label: "⏱ Expired",  cls: "bg-gray-100 text-gray-500 border-gray-300" },
};

// Tick positions on the progress bar, mirroring the backend's default
// `milestone_fractions` setting (fractions of the entry→T1 span).
const MILESTONE_TICKS = [0.25, 0.5, 0.75];

function fmt(n, dec = 2) {
  if (n == null) return "—";
  return parseFloat(n).toFixed(dec);
}

function pctFrom(entry, target) {
  if (entry == null || target == null || entry === 0) return null;
  return ((target - entry) / entry * 100).toFixed(1);
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Math.floor((Date.now() - new Date(isoStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function fmtExpiry(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

// Extract SR level source + price from trigger_reason for SR_TOUCH/SR_BREAKOUT.
// "SUPPORT touch: Cluster (103x) (Cluster) @ 57399.1" -> "Cluster @ 57399"
function srLevelLabel(triggerReason) {
  if (!triggerReason) return null;
  const src   = (triggerReason.match(/\b(Cluster|Pivot|Swing|Round)\b/i) || [])[0];
  const price = (triggerReason.match(/@\s*([\d.]+)/) || [])[1];
  if (!src && !price) return null;
  return `${src || "SR"}${price ? ` @ ${Math.round(parseFloat(price))}` : ""}`;
}

// TIMER v2 predictive-coil detail.
// "TIMER v2 coil: 15b squeeze, vol BUILDING, Q8.9 @ 584.98-585.42" -> "Q8.9 · 15b coil"
function timerV2Label(triggerReason) {
  if (!triggerReason) return null;
  const bars = (triggerReason.match(/(\d+)b\s+squeeze/i) || [])[1];
  const q    = (triggerReason.match(/\bQ([\d.]+)/) || [])[1];
  if (!bars && !q) return null;
  return [q ? `Q${q}` : null, bars ? `${bars}b coil` : null].filter(Boolean).join(" · ");
}

function swingBreakLabel(triggerReason) {
  if (!triggerReason) return null;
  const side = (triggerReason.match(/broke\s+swing\s+(low|high)/i) || [])[1];
  const price = (triggerReason.match(/@\s*([\d.]+)/) || [])[1];
  const when = (triggerReason.match(/at\s+(\d{2}:\d{2})\s*IST/i) || [])[1];
  if (!side && !price && !when) return null;
  const sideTxt = side ? (side.toUpperCase() === "LOW" ? "Low" : "High") : "Level";
  const priceTxt = price ? ` @ ${Math.round(parseFloat(price))}` : "";
  const whenTxt = when ? ` · ${when}` : "";
  return `${sideTxt}${priceTxt}${whenTxt}`;
}

export default function SignalCard({ signal, selected, onClick, onExit, marketOpen = false }) {
  const [exitArmed, setExitArmed] = useState(false);
  const [exiting, setExiting]     = useState(false);
  const [exitError, setExitError] = useState(null);

  const outcome = (signal.t1_hit && !signal.outcome)
    ? OUTCOME_STYLE.T1_HIT
    : OUTCOME_STYLE[signal.outcome];
  const isPending = !signal.outcome && !signal.t1_hit;
  const isMultiLeg = signal.legs && signal.legs.length > 1;
  const isCredit = isMultiLeg && signal.net_premium != null && parseFloat(signal.net_premium) < 0;
  const isStock = signal.instrument_type === "STOCK";
  // Direction inferred from option_type; multi-leg = NEUTRAL
  const direction = isMultiLeg ? "NEUTRAL"
    : signal.option_type === "CE" ? "BULLISH"
    : signal.option_type === "PE" ? "BEARISH" : null;
  // SR/Swing triggers always threshold 7.0 (raised from 6.0, Nov 3 2026); stock timer/event 9.0; index 7.5
  const isSrTrigger = signal.trigger_type === "SR_TOUCH" || signal.trigger_type === "SR_BREAKOUT" || signal.trigger_type === "SWING_BREAKOUT";
  const maxScore = 12;
  const scoreGood = isSrTrigger ? 7.0 : isStock ? 9.0 : 7.5;
  const scoreWarn = isSrTrigger ? 6.0 : isStock ? 8.0 : 6.5;
  const isCompression = signal.trigger_type === "COMPRESSION_BREAKOUT";
  const isSrTouch = signal.trigger_type === "SR_TOUCH";
  const isSrBreakout = signal.trigger_type === "SR_BREAKOUT";
  const isSwingBreakout = signal.trigger_type === "SWING_BREAKOUT";
  const isTimerV2 = signal.trigger_type === "TIMER_V2";
  const isFlatRange = signal.market_state === "FLAT_RANGE" || signal.market_state === "POST_GAP_FLAT";

  const entryMid = (signal.entry_low != null && signal.entry_high != null)
    ? (parseFloat(signal.entry_low) + parseFloat(signal.entry_high)) / 2
    : signal.entry_low != null ? parseFloat(signal.entry_low) : null;

  const slPct = pctFrom(entryMid, signal.stop_loss);
  const t1Pct = pctFrom(entryMid, signal.target_1);
  const t2Pct = pctFrom(entryMid, signal.target_2);

  const borderColor = signal.outcome === "WINNER" || signal.outcome === "BIG_WINNER" || (signal.t1_hit && !signal.outcome)
    ? "border-green-300"
    : signal.outcome === "LOSER" ? "border-red-300"
    : isPending ? "border-yellow-300"
    : "border-gray-200";

  return (
    <div
      onClick={onClick}
      className={`rounded-xl border p-3 cursor-pointer transition-all active:scale-[0.99]
        ${selected ? "border-blue-400 bg-blue-50" : `bg-white ${borderColor}`}`}
    >
      {/* ── Header row ── */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {/* Line 1: instrument + strike */}
          <div className="flex items-center gap-1.5">
            <span className="font-bold text-gray-900 text-sm">{signal.instrument}</span>
            {signal.strike && signal.option_type && (
              <span className="text-sm font-mono font-semibold text-gray-700">
                {signal.strike} {signal.option_type}
              </span>
            )}
          </div>
          {/* Line 2: all chips — expiry · stock/index · strategy · intraday/swing · direction · special */}
          <div className="flex items-center gap-1 flex-wrap mt-1">
            {signal.expiry && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-500">
                {fmtExpiry(signal.expiry)}
              </span>
            )}
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium
              ${isStock ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
              {signal.instrument_type ?? (isStock ? "STOCK" : "INDEX")}
            </span>
            {signal.strategy_type && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-gray-100 text-gray-500">
                {signal.strategy_type}
              </span>
            )}
            {signal.hold_days != null && (
              <span className={`text-xs px-1.5 py-0.5 rounded font-medium
                ${signal.hold_days === 1
                  ? "bg-orange-100 text-orange-700"
                  : signal.hold_days <= 3
                  ? "bg-teal-100 text-teal-700"
                  : "bg-indigo-100 text-indigo-700"}`}>
                {signal.hold_days === 1 ? "Intraday" : `Swing +${signal.hold_days}d`}
              </span>
            )}
            {direction === "BULLISH" && (
              <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-green-100 text-green-700">▲ Bullish</span>
            )}
            {direction === "BEARISH" && (
              <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-red-100 text-red-700">▼ Bearish</span>
            )}
            {isCompression && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-700 border border-amber-300">
                🚀 Breakout
              </span>
            )}
            {isSrTouch && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-700">
                📍 SR{srLevelLabel(signal.trigger_reason) ? ` · ${srLevelLabel(signal.trigger_reason)}` : ""}
              </span>
            )}
            {isSrBreakout && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-violet-100 text-violet-700">
                🔷 SR-BO{srLevelLabel(signal.trigger_reason) ? ` · ${srLevelLabel(signal.trigger_reason)}` : ""}
              </span>
            )}
            {isSwingBreakout && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-indigo-100 text-indigo-700">
                📉 SWING-BO{swingBreakLabel(signal.trigger_reason) ? ` · ${swingBreakLabel(signal.trigger_reason)}` : ""}
              </span>
            )}
            {isTimerV2 && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-cyan-100 text-cyan-700 border border-cyan-300">
                🌀 TIMER-V2{timerV2Label(signal.trigger_reason) ? ` · ${timerV2Label(signal.trigger_reason)}` : ""}
              </span>
            )}
            {isFlatRange && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-sky-100 text-sky-700">
                〰 {signal.market_state === "POST_GAP_FLAT" ? "Gap Flat" : "Flat Range"}
              </span>
            )}
            {isCredit && (
              <span className="text-xs px-1.5 py-0.5 rounded font-medium bg-purple-100 text-purple-700">
                Credit
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-shrink-0">
          {signal.score != null && (
            <span className={`text-xs font-mono px-1.5 py-0.5 rounded font-semibold
              ${signal.score >= scoreGood ? "bg-green-100 text-green-700" :
                signal.score >= scoreWarn ? "bg-yellow-100 text-yellow-700" :
                "bg-gray-100 text-gray-500"}`}>
              {signal.score.toFixed(1)}/{maxScore}
            </span>
          )}
          {isPending && signal.t1_hit && (
            <span className="text-xs px-2 py-0.5 rounded border bg-teal-50 text-teal-700 border-teal-300 font-medium">T1✓ Trailing</span>
          )}
          {signal.manual_exit && (
            <span className="text-xs px-2 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300">🚪 Manual</span>
          )}
          {outcome ? (
            <span className={`text-xs px-2 py-0.5 rounded border ${outcome.cls}`}>{outcome.label}</span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded border bg-yellow-50 text-yellow-700 border-yellow-300">Pending</span>
          )}
        </div>
      </div>

      {/* ── Price levels (single-leg) ── */}
      {!isMultiLeg && (
        <div className="mt-2.5 grid grid-cols-4 gap-1.5">
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-center">
            <p className="text-gray-400 text-xs leading-none mb-0.5">Buy</p>
            <p className="text-gray-800 font-mono text-xs font-semibold leading-none">
              {signal.entry_low != null ? fmt(signal.entry_low) : "—"}
            </p>
            {signal.entry_high != null && signal.entry_low !== signal.entry_high && (
              <p className="text-gray-400 font-mono text-xs leading-none">–{fmt(signal.entry_high)}</p>
            )}
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 text-center">
            <p className="text-gray-400 text-xs leading-none mb-0.5">SL</p>
            <p className="text-red-600 font-mono text-xs font-semibold leading-none">{fmt(signal.stop_loss)}</p>
            {slPct != null && <p className="text-red-400 text-xs leading-none">{slPct}%</p>}
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg px-2 py-1.5 text-center">
            <p className="text-gray-400 text-xs leading-none mb-0.5">T1</p>
            <p className="text-green-600 font-mono text-xs font-semibold leading-none">{fmt(signal.target_1)}</p>
            {t1Pct != null && <p className="text-green-500 text-xs leading-none">+{t1Pct}%</p>}
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 text-center">
            <p className="text-gray-400 text-xs leading-none mb-0.5">T2</p>
            <p className="text-emerald-600 font-mono text-xs font-semibold leading-none">{fmt(signal.target_2)}</p>
            {t2Pct != null && <p className="text-emerald-500 text-xs leading-none">+{t2Pct}%</p>}
          </div>
        </div>
      )}

      {/* ── Live progress to T1 (open single-leg signals with a live price) ── */}
      {!isMultiLeg && !signal.outcome && signal.current_ltp != null && signal.t1_progress_pct != null && (() => {
        const prog = Math.max(0, Math.min(100, signal.t1_progress_pct));
        const up = (signal.unrealized_pct ?? 0) >= 0;
        // milestones_hit is append-only; use it to fill the ladder ticks.
        const hitFracs = (signal.milestones_hit || []).map(m => m.frac);
        const ticks = MILESTONE_TICKS.map(f => ({
          frac: f,
          hit: hitFracs.some(h => Math.abs(h - f) < 0.001) || prog >= f * 100,
        }));
        return (
          <div className="mt-2">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-400">
                Live <span className="font-mono text-gray-600">{fmt(signal.current_ltp)}</span>
              </span>
              <span className="text-gray-400">
                {signal.unrealized_pct != null && (
                  <span className={`font-mono font-semibold ${up ? "text-green-600" : "text-red-600"}`}>
                    {up ? "+" : ""}{signal.unrealized_pct.toFixed(1)}%
                  </span>
                )}
                <span className="ml-1.5">{prog.toFixed(0)}% to T1</span>
              </span>
            </div>
            <div className="relative h-2 rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 rounded-full ${up ? "bg-green-500" : "bg-red-400"}`}
                style={{ width: `${prog}%` }}
              />
              {ticks.map(({ frac, hit }) => (
                <span
                  key={frac}
                  title={`${Math.round(frac * 100)}% to T1`}
                  className={`absolute top-0 bottom-0 w-0.5 ${hit ? "bg-green-700" : "bg-gray-300"}`}
                  style={{ left: `${frac * 100}%` }}
                />
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── R:R display (single-leg, active, no T1 hit) ── */}
      {!isMultiLeg && !signal.t1_hit && !signal.outcome && entryMid != null && (() => {
        const isl = signal.initial_stop_loss != null
          ? parseFloat(signal.initial_stop_loss)
          : signal.stop_loss != null ? parseFloat(signal.stop_loss) : null;
        if (!isl || isl >= entryMid) return null;
        const risk = entryMid - isl;
        const rr1 = signal.target_1 != null ? ((parseFloat(signal.target_1) - entryMid) / risk).toFixed(1) : null;
        const rr2 = signal.target_2 != null ? ((parseFloat(signal.target_2) - entryMid) / risk).toFixed(1) : null;
        if (!rr1) return null;
        return (
          <div className="mt-1.5 flex items-center gap-3 text-xs">
            <span className="text-gray-400 font-medium">R:R</span>
            <span className="font-mono text-blue-600 font-semibold">1:{rr1}<span className="text-gray-400 font-normal"> to T1</span></span>
            {rr2 && <span className="font-mono text-indigo-600 font-semibold">1:{rr2}<span className="text-gray-400 font-normal"> to T2</span></span>}
          </div>
        );
      })()}

      {/* ── Multi-leg summary ── */}
      {isMultiLeg && signal.net_premium != null && (() => {
        const net = parseFloat(signal.net_premium);
        const sl  = signal.stop_loss  != null ? parseFloat(signal.stop_loss)  : null;
        const t1  = signal.target_1   != null ? parseFloat(signal.target_1)   : null;
        const t2  = signal.target_2   != null ? parseFloat(signal.target_2)   : null;
        // For credit: profit = net becomes less negative. Show keep% of premium.
        const keepPctT1 = isCredit && sl != null && t1 != null
          ? Math.round((1 - (t1 / net)) * 100) : null;
        const keepPctT2 = isCredit && sl != null && t2 != null
          ? Math.round((1 - (t2 / net)) * 100) : null;
        const lossPct = isCredit && sl != null
          ? Math.round(((sl - net) / Math.abs(net)) * 100) : null;
        return (
          <div className="mt-2 grid grid-cols-4 gap-1.5">
            <div className="bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 text-center">
              <p className="text-gray-400 text-xs leading-none mb-0.5">{isCredit ? "Rcvd" : "Net"}</p>
              <p className="text-gray-800 font-mono text-xs font-semibold">₹{fmt(Math.abs(net))}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-2 py-1.5 text-center">
              <p className="text-gray-400 text-xs leading-none mb-0.5">SL</p>
              <p className="text-red-600 font-mono text-xs font-semibold">{sl != null ? fmt(Math.abs(sl)) : "—"}</p>
              {lossPct != null && <p className="text-red-400 text-xs">lose {lossPct}%</p>}
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg px-2 py-1.5 text-center">
              <p className="text-gray-400 text-xs leading-none mb-0.5">T1</p>
              <p className="text-green-600 font-mono text-xs font-semibold">{t1 != null ? fmt(Math.abs(t1)) : "—"}</p>
              {keepPctT1 != null && <p className="text-green-500 text-xs">keep {keepPctT1}%</p>}
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5 text-center">
              <p className="text-gray-400 text-xs leading-none mb-0.5">T2</p>
              <p className="text-emerald-600 font-mono text-xs font-semibold">{t2 != null ? fmt(Math.abs(t2)) : "—"}</p>
              {keepPctT2 != null && <p className="text-emerald-500 text-xs">keep {keepPctT2}%</p>}
            </div>
          </div>
        );
      })()}

      {/* ── Footer ── */}
      <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
        <span>{timeAgo(signal.created_at)}</span>
        {signal.return_pct != null ? (
          <span className={`font-mono font-semibold ${signal.return_pct >= 0 ? "text-green-600" : "text-red-600"}`}>
            {signal.return_pct >= 0 ? "+" : ""}{signal.return_pct.toFixed(1)}%
          </span>
        ) : (
          <span className="text-gray-300 text-xs">tap for details</span>
        )}
      </div>

      {/* ── Expanded detail ── */}
      {selected && (
        <div className="mt-3 pt-3 border-t border-gray-200 space-y-3" onClick={e => e.stopPropagation()}>
          <div className="grid grid-cols-3 gap-2 text-xs">
            {signal.confidence_pct != null && (
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-gray-400 leading-none mb-0.5">Confidence</p>
                <p className="text-gray-700 font-semibold">{signal.confidence_pct}%</p>
              </div>
            )}
            {signal.trigger_type && (
              <div className={`rounded-lg p-2 text-center ${isCompression ? "bg-amber-50" : (isSrTouch || isSrBreakout) ? "bg-violet-50" : isSwingBreakout ? "bg-indigo-50" : isTimerV2 ? "bg-cyan-50" : "bg-gray-50"}`}>
                <p className="text-gray-400 leading-none mb-0.5">Trigger</p>
                <p className={`font-semibold text-[10px] leading-tight ${isCompression ? "text-amber-700" : (isSrTouch || isSrBreakout) ? "text-violet-700" : isSwingBreakout ? "text-indigo-700" : isTimerV2 ? "text-cyan-700" : "text-gray-700"}`}>{signal.trigger_type}</p>
              </div>
            )}
            {signal.minutes_to_outcome != null && (
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-gray-400 leading-none mb-0.5">Duration</p>
                <p className="text-gray-700 font-semibold">{signal.minutes_to_outcome}m</p>
              </div>
            )}
          </div>
          {(signal.setup_type || signal.trade_structure) && (
            <div className="flex gap-2 text-xs">
              {signal.setup_type && (
                <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">{signal.setup_type}</span>
              )}
              {signal.trade_structure && (
                <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-500 font-mono">{signal.trade_structure}</span>
              )}
            </div>
          )}

          {signal.score_breakdown?.text && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Score Breakdown</p>
              <pre className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap font-mono bg-gray-50 border border-gray-200 rounded-lg p-2.5">{signal.score_breakdown.text}</pre>
            </div>
          )}

          {signal.claude_reasoning && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Analysis</p>
              <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">{signal.claude_reasoning}</p>
            </div>
          )}

          {signal.risk_warning && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
              <p className="text-xs text-orange-700 font-semibold mb-0.5">⚠ Risk Warning</p>
              <p className="text-xs text-orange-600 leading-relaxed">{signal.risk_warning}</p>
            </div>
          )}

          {/* ── Legs table (multi-leg only) ── */}
          {isMultiLeg && signal.legs && signal.legs.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Legs</p>
              {isCredit && (
                <p className="text-xs text-purple-600 mb-1.5">
                  Credit strategy — premium received. Profit when net premium decays toward zero.
                </p>
              )}
              <div className="space-y-1">
                {signal.legs.map((leg, i) => (
                  <div key={i} className={`flex items-center justify-between text-xs px-2.5 py-1.5 rounded-lg
                    ${leg.action === "BUY"
                      ? "bg-green-50 border border-green-200"
                      : "bg-red-50 border border-red-200"}`}>
                    <span className={`font-bold w-10 ${leg.action === "BUY" ? "text-green-700" : "text-red-700"}`}>
                      {leg.action}
                    </span>
                    <span className="font-mono text-gray-800 flex-1 text-center text-[11px]">
                      {leg.instrument}
                    </span>
                    {leg.premium != null && (
                      <span className="font-mono text-gray-500">₹{fmt(leg.premium)}</span>
                    )}
                  </div>
                ))}
              </div>
              {(signal.max_profit != null || signal.max_loss != null) && (
                <div className="flex gap-4 mt-2 text-xs">
                  {signal.max_profit != null && (
                    <span className="text-green-600 font-mono font-semibold">
                      Max Profit ₹{fmt(signal.max_profit, 0)}/lot
                    </span>
                  )}
                  {signal.max_loss != null && (
                    <span className="text-red-600 font-mono font-semibold">
                      Max Loss ₹{fmt(signal.max_loss, 0)}/lot
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Manual exit (open single-leg signals, market hours only) ── */}
          {!signal.outcome && !isMultiLeg && (
            <div className="pt-1">
              {!marketOpen ? (
                <p className="text-xs text-gray-400">
                  Manual exit is available during market hours only.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <button
                      disabled={exiting}
                      onClick={async () => {
                        setExitError(null);
                        if (!exitArmed) { setExitArmed(true); return; }
                        setExiting(true);
                        try {
                          const res = await fetch(`${API}/api/signals/${signal.id}/exit`, {
                            method: "POST",
                            headers: { "X-Api-Key": process.env.REACT_APP_DASHBOARD_KEY || "" },
                          });
                          const body = await res.json().catch(() => ({}));
                          if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
                          setExitArmed(false);
                          onExit?.();
                        } catch (err) {
                          setExitError(String(err.message || err));
                        } finally {
                          setExiting(false);
                        }
                      }}
                      className={`px-3 py-1 text-xs rounded border font-medium disabled:opacity-40 disabled:cursor-not-allowed
                        ${exitArmed
                          ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                          : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}
                    >
                      {exiting ? "Exiting…" : exitArmed ? "Confirm exit?" : "Exit Trade"}
                    </button>
                    {exitArmed && !exiting && (
                      <button
                        onClick={() => { setExitArmed(false); setExitError(null); }}
                        className="px-3 py-1 text-xs rounded border border-gray-300 text-gray-500 hover:bg-gray-100"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Closes this alert at the current price. Square off in your broker separately.
                  </p>
                  {exitError && (
                    <p className="text-xs text-red-600 mt-1">Exit failed: {exitError}</p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

